import { useCallback, useEffect, useMemo, useState } from 'react';
import { useApi } from '../lib/api';
import type { AttributeDef } from '../lib/types';
import { Banner, Empty, ErrorBox, Field, Loading, Table, Toolbar } from '../ui';

interface ApiError {
  error?: string;
}

type Draft = Pick<AttributeDef, 'label' | 'abbr' | 'unit' | 'type' | 'criteriaId' | 'visible' | 'sortOrder'>;

const toDraft = (a: AttributeDef): Draft => ({
  label: a.label,
  abbr: a.abbr,
  unit: a.unit,
  type: a.type,
  criteriaId: a.criteriaId,
  visible: a.visible,
  sortOrder: a.sortOrder,
});

/**
 * The attribute dictionary: how each product attribute is printed in the spec
 * table of the articles we inject into TecDoc.
 *
 * An import that brings a column nobody has described adds it here under a label
 * made from its key; this is where it gets its real label and unit. Changes are
 * live on the next injected article — there is nothing to redeploy.
 */
export default function Attributes() {
  const { request } = useApi();
  const [items, setItems] = useState<AttributeDef[] | null>(null);
  const [err, setErr] = useState('');
  const [q, setQ] = useState('');
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [busy, setBusy] = useState(false);
  const [newKey, setNewKey] = useState('');

  const load = useCallback(async () => {
    const r = await request<{ items: AttributeDef[] } | ApiError>('GET', '/v1/admin/attributes');
    if (r.data && 'items' in r.data) setItems(r.data.items);
    else setErr((r.data as ApiError | null)?.error ?? `Could not load attributes (HTTP ${r.status}).`);
  }, [request]);

  useEffect(() => {
    void load();
  }, [load]);

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!items || !needle) return items;
    return items.filter((a) => a.key.includes(needle) || a.label.toLowerCase().includes(needle));
  }, [items, q]);

  function edit(a: AttributeDef) {
    setEditing(a.key);
    setDraft(toDraft(a));
  }

  async function save(key: string, body: Partial<Draft>) {
    setErr('');
    setBusy(true);
    const r = await request<ApiError>('PUT', `/v1/admin/attributes/${encodeURIComponent(key)}`, body);
    setBusy(false);
    if (!r.ok) {
      setErr(r.data?.error ?? `Save failed (HTTP ${r.status}).`);
      return false;
    }
    setEditing(null);
    setDraft(null);
    await load();
    return true;
  }

  async function add() {
    const key = newKey.trim().toLowerCase();
    if (!key) return;
    if (await save(key, {})) setNewKey('');
  }

  async function forget(key: string) {
    if (!window.confirm(`Forget the description of "${key}"? Products keep the value; it prints under "${key}" again.`)) return;
    setBusy(true);
    const r = await request<ApiError>('DELETE', `/v1/admin/attributes/${encodeURIComponent(key)}`);
    setBusy(false);
    if (!r.ok) setErr(r.data?.error ?? `Delete failed (HTTP ${r.status}).`);
    await load();
  }

  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setDraft((d) => (d ? { ...d, [k]: v } : d));
  const opt = (v: string): string | null => (v.trim() === '' ? null : v);

  return (
    <div className="blk !mb-0">
      <div className="blk-head">
        <h3 className="text-[13px] font-semibold">Attributes</h3>
        <span className="text-[11.5px] text-faint">how product attributes print in TecDoc&apos;s spec table</span>
      </div>
      <div className="blk-body">
        <Banner icon="≡">
          The label is what the customer reads (<code>Tensione [V]</code>), as TecDoc writes it in Italian. Leave
          the criteria id at 0 unless you have seen TecDoc&apos;s own id on a live article. Untick <b>Printed</b> for
          values that are carried on the product but are not specifications, such as a supplier&apos;s internal code.
          New import columns appear here on their own.
        </Banner>
        <Toolbar>
          <Field label="Filter">
            <input className="input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="key or label" />
          </Field>
          <span className="flex-1" />
          <Field label="New key">
            <input
              className="input mono"
              value={newKey}
              onChange={(e) => setNewKey(e.target.value)}
              placeholder="e.g. thread_size"
              style={{ width: 160 }}
            />
          </Field>
          <button className="btn btn-sm" disabled={busy || !newKey.trim()} onClick={() => void add()}>
            Add
          </button>
        </Toolbar>
        {err && <ErrorBox>{err}</ErrorBox>}
        {shown === null ? (
          <Loading />
        ) : shown.length === 0 ? (
          <Empty>No attributes.</Empty>
        ) : (
          <Table cols={['Key', 'Label', 'Short label', 'Unit', 'Type', 'Criteria id', 'Printed', 'Order', 'Products', '']}>
            {shown.map((a) =>
              editing === a.key && draft ? (
                <tr key={a.key}>
                  <td className="mono text-[11.5px]">{a.key}</td>
                  <td>
                    <input className="input" value={draft.label} onChange={(e) => set('label', e.target.value)} />
                  </td>
                  <td>
                    <input className="input" value={draft.abbr ?? ''} onChange={(e) => set('abbr', opt(e.target.value))} />
                  </td>
                  <td>
                    <input
                      className="input"
                      value={draft.unit ?? ''}
                      onChange={(e) => set('unit', opt(e.target.value))}
                      style={{ width: 70 }}
                    />
                  </td>
                  <td>
                    <select
                      className="input"
                      value={draft.type ?? ''}
                      onChange={(e) => set('type', (e.target.value || null) as Draft['type'])}
                      style={{ width: 'auto' }}
                    >
                      <option value="">auto</option>
                      <option value="N">number</option>
                      <option value="A">text</option>
                    </select>
                  </td>
                  <td>
                    <input
                      className="input mono"
                      type="number"
                      min={0}
                      value={draft.criteriaId}
                      onChange={(e) => set('criteriaId', Number(e.target.value) || 0)}
                      style={{ width: 80 }}
                    />
                  </td>
                  <td>
                    <input type="checkbox" checked={draft.visible} onChange={(e) => set('visible', e.target.checked)} />
                  </td>
                  <td>
                    <input
                      className="input mono"
                      type="number"
                      value={draft.sortOrder}
                      onChange={(e) => set('sortOrder', Number(e.target.value) || 0)}
                      style={{ width: 70 }}
                    />
                  </td>
                  <td>{a.products}</td>
                  <td className="text-right">
                    <div className="flex justify-end gap-1.5">
                      <button
                        className="btn btn-sm btn-primary"
                        disabled={busy || !draft.label.trim()}
                        onClick={() => void save(a.key, draft)}
                      >
                        Save
                      </button>
                      <button className="btn btn-sm" disabled={busy} onClick={() => setEditing(null)}>
                        Cancel
                      </button>
                    </div>
                  </td>
                </tr>
              ) : (
                <tr key={a.key}>
                  <td className="mono text-[11.5px]">{a.key}</td>
                  <td>{a.label}</td>
                  <td className="text-muted">{a.abbr ?? <span className="text-faint">—</span>}</td>
                  <td className="text-muted">{a.unit ?? <span className="text-faint">—</span>}</td>
                  <td className="text-muted">{a.type === 'N' ? 'number' : a.type === 'A' ? 'text' : 'auto'}</td>
                  <td className="mono text-muted">{a.criteriaId || <span className="text-faint">0</span>}</td>
                  <td>{a.visible ? '✓' : <span className="text-faint">hidden</span>}</td>
                  <td className="mono text-faint">{a.sortOrder}</td>
                  <td>{a.products || <span className="text-faint">0</span>}</td>
                  <td className="text-right">
                    <div className="flex justify-end gap-1.5">
                      <button className="btn btn-sm" disabled={busy} onClick={() => edit(a)}>
                        Edit
                      </button>
                      <button className="btn btn-sm" disabled={busy} onClick={() => void forget(a.key)}>
                        Forget
                      </button>
                    </div>
                  </td>
                </tr>
              ),
            )}
          </Table>
        )}
      </div>
    </div>
  );
}
